import { CSSProperties } from 'react';
import useWebsiteBuilder, {
    VideoContentStyle,
    WebsiteBuilderContent,
    WebsiteBuilderStyle
} from 'Hooks/useWebsiteBuilder';
import Colors from 'modules/Colors';
import { styx } from 'modules/Utils';
import { pullVideoIdFromUrl } from 'helpers/YouTube';

interface VideoPreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
}

const VideoPreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
}: VideoPreviewContentProps) => {
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;

    const {
        backgroundColor: backgroundColorWebsiteStyle,
    } = websiteStyle;

    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled;
    const contentBackgroundColor = websiteContent[contentIndex].contentBackgroundColor;
    const backgroundColor = isContentBackgroundColorEnabled ? contentBackgroundColor || backgroundColorWebsiteStyle : backgroundColorWebsiteStyle;

    const videoContent = websiteContent[contentIndex].videoContent;

    if (!videoContent) {
        return null;
    }

    const isYouTubeVideo = videoContent.video && (videoContent.video.includes('youtube.com') || videoContent.video.includes('youtu.be'));

    const getYouTubeEmbedUrl = (url: string) => {
        if (!url) {
            return 'https://placehold.co/400';
        }

        const videoId = pullVideoIdFromUrl(url);
        if (!videoId) {
            return url;
        }

        return `https://www.youtube.com/embed/${videoId}`;
    };

    const getVideoContentStyle = () => {
        const {
            style,
        } = videoContent || {};

        switch (style) {
            case VideoContentStyle.FULL_SCREEN:
                return {
                    outer: {
                        paddingLeft: 0,
                        paddingRight: 0,
                    },
                    inner: {
                        borderRadius: 0,
                    }
                };
            case VideoContentStyle.SQUARE:
                return {
                    outer: {},
                    inner: {
                        borderRadius: 0,
                    }
                };
            case VideoContentStyle.ROUNDED:
                return {
                    outer: {},
                    inner: {
                        borderRadius: 16,
                    }
                };
            case VideoContentStyle.CIRCULAR:
                return {
                    outer: {},
                    inner: {
                        borderRadius: 1000,
                    }
                };
            default:
                return {
                    outer: {},
                    inner: {}
                };
        }
    }

    // NOTE: render()
    return (
        <div
            className="videoPreviewContentComponent"
            style={styx(
                sty.videoPreviewContentComponent,
                getVideoContentStyle().outer,
                { backgroundColor },
                styleProp,
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            {isYouTubeVideo ?
                <iframe
                    className="videoPreviewContent"
                    style={styx(
                        sty.videoPreviewContent,
                        { aspectRatio: 16 / 9 },
                        getVideoContentStyle().inner,
                        {
                            objectFit: 'cover',
                        }
                    )}
                    src={getYouTubeEmbedUrl(videoContent.video)}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
                :
                <video
                    className="videoPreviewContent"
                    playsInline
                    autoPlay
                    muted
                    loop
                    style={styx(
                        sty.videoPreviewContent,
                        getVideoContentStyle().inner,
                        {
                            objectFit: 'cover',
                        }
                    )}
                    src={videoContent?.video || 'https://placehold.co/400'}
                />
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    videoPreviewContentComponent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingLeft: 16,
        paddingRight: 16,
    },
    videoPreviewContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'black',
        width: '100%',
        backgroundColor: Colors.grey,
        borderRadius: 16,
    }
}

export default VideoPreviewContent;